import * as React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { css, useTheme } from '@emotion/react';
import { autoPlacement, FloatingOverlay } from '@floating-ui/react';
import { Box, CloseAlt, Draft, IconButton, Inline, Progress, Text, useBreakpoint } from '@resi-media/resi-ui';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router';
import { match, P } from 'ts-pattern';
import { useOnClickOutside } from 'usehooks-ts';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import { useSearch } from '@sites/contexts/searchContext';
import generateUrl from '@sites/helpers/generateUrl';
import highlightSuggestion from '@sites/helpers/highlightSuggestion';
import { useHeaderVisibility } from '@sites/hooks/useHeaderVisibility';
import { usePrefix } from '@studio/hooks/use-prefix';
import { SearchList } from '../SearchList';
import { SearchListItem } from '../SearchListItem';

const SearchInput = () => {
  const mediaQuery = useBreakpoint();
  const { commonT, prefixNS } = usePrefix('components:', 'search');
  const theme = useTheme();
  const {
    isSearchInputExpanded,
    isSearchPopoverOpen,
    queries,
    query,
    removePastQuery,
    setQueryParams,
    setSearch,
    setSearchInputExpanded,
    setSearchPopoverOpen,
    suggestions,
  } = useSearch();

  // Refs for DOM elements
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);

  // Navigation and routing
  const { customerSite = '' } = useParams<'customerSite'>();
  const navigate = useNavigate();
  const searchPath = '/:customerSite/search';
  const isHeaderVisible = useHeaderVisibility();

  // Effect to close the search when header is not visible
  React.useEffect(() => {
    if (!isHeaderVisible) {
      setSearchPopoverOpen(false);
      setAnchorEl(null);
    }
  }, [isHeaderVisible, setSearchInputExpanded, setSearchPopoverOpen]);

  // Handle search submission
  const handleSubmit = (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      // Track search term for analytics
      gtag('event', GOOGLE_EVENTS.SEARCH_SUBMIT, { search_term: trimmedValue, slug: customerSite });

      // Set the search query
      setSearch({ query: trimmedValue });

      // Close the popover but keep the input expanded
      setAnchorEl(null);
      setSearchPopoverOpen(false);
      setSearchInputExpanded(true);

      inputRef.current?.blur();
      navigate(generateUrl(searchPath, { customerSite }, { q: trimmedValue }));
    }
  };

  const clearInput = () => {
    setQueryParams('');
  };

  const suggestionsToRender = React.useMemo(() => {
    if (!query.trim()) return [];

    const apiSuggestions = (suggestions.data.get(query) ?? []).map((suggestion) => ({
      label: highlightSuggestion(suggestion, query),
      value: suggestion,
    }));

    const removeDuplicates = apiSuggestions.some(
      (suggestion) => suggestion.value.trim().toLowerCase() === query.trim().toLowerCase()
    )
      ? apiSuggestions
      : [{ label: query, value: query }, ...apiSuggestions];

    return removeDuplicates.slice(0, 5);
  }, [query, suggestions.data]);

  const FOCUSED_ITEM_TYPES = {
    RECENT_SUGGESTION: 'recent-suggestion',
    SUGGESTION: 'suggestion',
    INPUT: 'input',
  } as const;
  type FocusedItem =
    | { index: number; type: typeof FOCUSED_ITEM_TYPES.RECENT_SUGGESTION }
    | { index: number; type: typeof FOCUSED_ITEM_TYPES.SUGGESTION }
    | { type: typeof FOCUSED_ITEM_TYPES.INPUT }
    | null;

  const [focusedItem, setFocusedItem] = React.useState<FocusedItem>(null);
  const suggestionRefs = React.useRef<Array<HTMLElement | null>>([]);
  const recentSuggestionRefs = React.useRef<Array<HTMLElement | null>>([]);

  React.useEffect(() => {
    setFocusedItem(null);
  }, [query]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setSearchInputExpanded(Boolean(query));
      setSearchPopoverOpen(false);
      setAnchorEl(null);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isSearchPopoverOpen) {
        setSearchPopoverOpen(true);
        setAnchorEl(inputRef.current);
      } else if (query.trim() && suggestionsToRender.length > 0) {
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.SUGGESTION, index: 0 });
        setTimeout(() => {
          suggestionRefs.current[0]?.focus();
          inputRef.current?.blur();
        }, 0);
      } else if (!query.trim() && queries.size > 0) {
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.RECENT_SUGGESTION, index: 0 });
        setTimeout(() => {
          recentSuggestionRefs.current[0]?.focus();
          inputRef.current?.blur();
        }, 0);
      }
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (suggestionsToRender.length > 0) {
        const lastIdx = suggestionsToRender.length - 1;
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.SUGGESTION, index: lastIdx });
        setTimeout(() => {
          suggestionRefs.current[lastIdx]?.focus();
          inputRef.current?.blur();
        }, 0);
      } else if (!query.trim() && queries.size > 0) {
        const lastIdx = Math.min(queries.size, 5) - 1;
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.RECENT_SUGGESTION, index: lastIdx });
        setTimeout(() => {
          recentSuggestionRefs.current[lastIdx]?.focus();
          inputRef.current?.blur();
        }, 0);
      }
    }
    if (event.key === 'Delete') {
      removePastQuery(event.currentTarget.value);
    }
    if (event.key === 'Enter') {
      handleSubmit(event.currentTarget.value);
    }
  };

  const handleCombinedSuggestionKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    index: number,
    items: (string | { label: React.ReactNode; value: string })[],
    type: typeof FOCUSED_ITEM_TYPES.RECENT_SUGGESTION | typeof FOCUSED_ITEM_TYPES.SUGGESTION
  ) => {
    if (event.key === 'ArrowUp') {
      if (focusedItem?.type === type && focusedItem.index === 0) {
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.INPUT });
        setAnchorEl(inputRef.current);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } else {
        setFocusedItem({ type, index: Math.max(0, index - 1) });
      }
    }
    if (event.key === 'ArrowDown') {
      if (index === items.length - 1) {
        setFocusedItem({ type: FOCUSED_ITEM_TYPES.INPUT });
        setAnchorEl(inputRef.current);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } else {
        setFocusedItem({ type, index: index + 1 });
      }
    }
    if (event.key === 'Enter') {
      if (items[index]) {
        const item = items[index];
        if (typeof item === 'string') {
          handleSubmit(item);
        } else {
          handleSubmit(item.value);
        }
      }
    }
  };

  useOnClickOutside([wrapperRef, popoverRef], () => {
    setSearchInputExpanded(Boolean(query));
    setSearchPopoverOpen(false);
    setAnchorEl(null);
  });

  return !isSearchInputExpanded ? (
    <IconButton
      css={(theme) => css`
        background: ${theme.palette.neutral.main};

        :hover:not(:disabled, &[data-${theme.prefix}-button-base--disabled]) {
          background: ${theme.palette.neutral.altBackground};
        }
      `}
      dataTestId="search-icon-button"
      label="search"
      onClick={() => {
        // When the search icon is clicked, we want to expand the input and focus it
        setSearchInputExpanded(true);

        // Schedule focus for after the component renders
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            // Only open the popover after the input is focused
            setSearchPopoverOpen(true);
            setAnchorEl(inputRef.current);
          }
        }, 0);
      }}
      type="button"
    >
      <SearchOutlined
        css={(ruiTheme) => css`
          color: ${ruiTheme.palette.common.white};
          font-size: 20px;
        `}
        data-testid="search-icon"
      />
    </IconButton>
  ) : (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <Draft.TextInput<'input'>
        ref={inputRef}
        contentEditable="false"
        css={(theme) => css`
          background-color: ${theme.palette.surface.canvas};
          width: ${!mediaQuery.lg ? '100%' : '385px'};
          border-radius: 4px;
          border: 1px solid ${theme.palette.text.disabled};
          :hover {
            border-color: ${theme.palette.text.disabled};
          }
          justify-content: flex-end;
          text-overflow: ellipsis;
          padding: ${theme.spacing.s} ${theme.spacing.x24} ${theme.spacing.s} ${theme.spacing.x3};
          ${isSearchPopoverOpen &&
          mediaQuery.lg && {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
        `}
        dataTestId="search-input"
        endNode={
          <Inline
            alignItems="center"
            gap={suggestions.isLoading ? 's' : 'x0'}
            style={{ position: 'relative', left: '7px' }}
          >
            {query &&
              (suggestions.isLoading ? (
                <Progress dataTestId="search-input-suggestions__loading" sizeVariant="xs" />
              ) : (
                <IconButton colorVariant="neutral" data-testid="clear-input" label="delete" onClick={clearInput}>
                  <CloseAlt style={{ cursor: 'pointer', fontSize: '10px' }} />
                </IconButton>
              ))}
            <SearchOutlined
              css={(theme) =>
                css({
                  backgroundColor: theme.palette.surface.disabled,
                  borderRadius: '0 4px 4px 0',
                  fontSize: '20px',
                  padding: '9px',
                  ...(isSearchPopoverOpen && {
                    borderRadius: '0 4px 0 0',
                  }),
                })
              }
              data-testid="search-input-submit-icon"
              onClick={() => handleSubmit(query)}
              tabIndex={0}
            />
          </Inline>
        }
        onChange={(ev) => {
          // Always set the anchor element to keep the popover open while typing
          setAnchorEl(inputRef.current);
          setSearchPopoverOpen(true);
          setQueryParams(ev.currentTarget.value);
          // Expand the input when typing
          setSearchInputExpanded(true);
        }}
        onFocus={() => {
          setFocusedItem(null);
          setAnchorEl(inputRef.current);
          setSearchPopoverOpen(true);
        }}
        onKeyDown={handleInputKeyDown}
        placeholder={prefixNS('placeholder')}
        spellCheck={false}
        value={query}
      />
      <Draft.Popover
        anchorEl={anchorEl}
        isOpen={isSearchPopoverOpen}
        middleware={[
          autoPlacement({
            rootBoundary: 'document',
            allowedPlacements: ['bottom'],
          }),
        ]}
        onOpenChange={() => {
          setSearchInputExpanded(Boolean(query));
          setSearchPopoverOpen(false);
          setAnchorEl(null);
        }}
        placement="bottom"
        useDismissProps={{
          escapeKey: true,
          outsidePress: true,
        }}
      >
        <Draft.PopoverContent
          ref={popoverRef}
          css={(theme) => css`
            width: ${!mediaQuery.lg ? 'calc(100vw - 40px)' : '385px'};
            position: absolute;
            border-radius: 0 0 ${theme.spacing.x3} ${theme.spacing.x3};
            background-color: ${theme.palette.surface.canvas};
            box-shadow: none;
            z-index: ${theme.zIndex.tooltip + 1};
            transition: visibility 0.3s ease;
            top: ${mediaQuery.lg ? '0px' : '-3px'};
          `}
          dataTestId="search-input-suggestions__popover"
          focusManagerProps={{
            closeOnFocusOut: false,
            returnFocus: false,
          }}
        >
          {match({ query: query.trim(), queries, suggestions: suggestionsToRender, isLoading: suggestions.isLoading })
            .with({ query: '', queries: P.when((s) => s.size > 0) }, ({ isLoading, queries: queriesToRender }) => {
              const items = [...Array.from(queriesToRender).slice(0, 5)];
              return (
                <SearchList dataTestId="recent-suggestions-list" isLoading={isLoading}>
                  <Box
                    as={Text}
                    bgColor={theme.palette.surface.canvas}
                    fontWeight="semiBold"
                    height="x10"
                    px="x4"
                    py="s"
                    variant="h5"
                  >
                    <Inline alignItems="center" css={css({ color: theme.palette.text.title })}>
                      <SearchOutlined
                        css={css`
                          color: ${theme.palette.contrast};
                          font-size: 20px;
                        `}
                        data-testid="search-icon--recent"
                      />
                      {prefixNS('recentSearches')}
                    </Inline>
                  </Box>
                  {items.map((value, i, arr) => (
                    <SearchListItem
                      key={value}
                      ref={(el: HTMLElement | null) => (recentSuggestionRefs.current[i] = el)}
                      aria-pressed={
                        focusedItem?.type === FOCUSED_ITEM_TYPES.RECENT_SUGGESTION && focusedItem.index === i
                      }
                      as={Link}
                      css={
                        arr.length - 1 === i
                          ? css({ borderRadius: `0 0 ${theme.spacing.x3} ${theme.spacing.x3}` })
                          : css({ borderRadius: 0 })
                      }
                      dataTestId={`search-input__recent-search-suggestion--${value}`}
                      endNode={
                        <IconButton
                          borderRadius="circle"
                          colorVariant="neutral"
                          dataTestId={`search-input__recent-search-suggestion-delete--${value}`}
                          height="2rem"
                          label={commonT('remove')}
                          onClick={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            removePastQuery(value);
                          }}
                          onTouchEnd={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            removePastQuery(value);
                          }}
                          title={commonT('remove')}
                          width="2rem"
                        >
                          <CloseAlt data-testid="delete-icon" style={{ fontSize: '10px' }} />
                        </IconButton>
                      }
                      onClick={() => {
                        setSearch({ query: value });
                        setSearchPopoverOpen(false);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) =>
                        handleCombinedSuggestionKeyDown(e, i, arr, FOCUSED_ITEM_TYPES.RECENT_SUGGESTION)
                      }
                      title={value}
                      to={generateUrl(searchPath, { customerSite }, { q: value })}
                    >
                      <Text css={css({ color: theme.palette.text.title })} truncate>
                        {value}
                      </Text>
                    </SearchListItem>
                  ))}
                </SearchList>
              );
            })
            .with({ query: '', queries: P.when((s) => s.size === 0) }, ({ isLoading }) => (
              <SearchList dataTestId="empty-suggestions-list" isLoading={isLoading}>
                <Box
                  as={Text}
                  css={css({ color: theme.palette.text.title })}
                  fontWeight="semiBold"
                  height="x10"
                  px="x4"
                  py="s"
                  variant="h5"
                >
                  <Inline alignItems="center">
                    <SearchOutlined
                      css={css`
                        color: ${theme.palette.contrast};
                        font-size: 20px;
                      `}
                      data-testid="search-icon--recent"
                    />
                    {prefixNS('recentSearches')}
                  </Inline>
                </Box>
                <Box
                  as={Text}
                  css={css({ color: theme.palette.text.title })}
                  fontSize="page"
                  height="auto"
                  px="x4"
                  py="s"
                >
                  {prefixNS('helpText')}
                </Box>
              </SearchList>
            ))
            .with(
              { query: P.not(''), suggestions: P.when((s) => s.length > 0) },
              ({ isLoading, suggestions: suggestionsToMap }) => {
                const items = suggestionsToMap;
                return (
                  <SearchList dataTestId="data-suggestions-list" isLoading={isLoading}>
                    {items.map((suggestion, i, arr) => (
                      <SearchListItem
                        key={suggestion.value}
                        ref={(el: HTMLElement | null) => (suggestionRefs.current[i] = el)}
                        aria-pressed={focusedItem?.type === FOCUSED_ITEM_TYPES.SUGGESTION && focusedItem.index === i}
                        as={Link}
                        css={arr.length - 1 === i ? css({ borderRadius: theme.spacing.x3 }) : css({ borderRadius: 0 })}
                        dataTestId={`search-input__suggestion--${suggestion.value}`}
                        onClick={() => {
                          handleSubmit(suggestion.value);
                        }}
                        onKeyDown={(e: React.KeyboardEvent<HTMLElement>) =>
                          handleCombinedSuggestionKeyDown(e, i, suggestionsToRender, FOCUSED_ITEM_TYPES.SUGGESTION)
                        }
                        startNode={
                          <SearchOutlined
                            css={css`
                              color: ${theme.palette.contrast};
                              font-size: 20px;
                            `}
                            data-testid="search-icon--suggestion"
                          />
                        }
                        title={suggestion.value}
                        to={generateUrl(searchPath, { customerSite }, { q: suggestion.value })}
                      >
                        <Draft.RichTextSanitized
                          isMarkdown
                          markdownProps={{
                            components: {
                              p(props) {
                                const { children, node: _node, ref: _ref, ...rest } = props;
                                return (
                                  <Text
                                    as="span"
                                    css={css({ color: theme.palette.text.title, display: 'block' })}
                                    truncate
                                    {...rest}
                                  >
                                    {children as React.ReactNode}
                                  </Text>
                                );
                              },
                            },
                          }}
                        >
                          {suggestion.label}
                        </Draft.RichTextSanitized>
                      </SearchListItem>
                    ))}
                  </SearchList>
                );
              }
            )
            .otherwise(() => null)}
        </Draft.PopoverContent>
      </Draft.Popover>

      {/* Mobile overlay */}
      {Boolean(anchorEl) &&
        isSearchPopoverOpen &&
        !mediaQuery.lg &&
        createPortal(
          <FloatingOverlay
            data-testid="search-overlay"
            onClick={() => setSearchPopoverOpen(false)}
            style={{ background: 'rgba(0,0,0, 0.40)', marginTop: theme.spacing.x20 }}
          />,
          document.body
        )}
    </div>
  );
};

SearchInput.displayName = 'SearchInput';

export { SearchInput };
